import { useState } from "react";

import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import {
  FAVICON_MIN_SIZE,
  getFaviconUrl,
  getIconHost,
  getSiteInitial,
  getSiteTileColor,
} from "@demo/libs/tool-calls/favicon";

export interface FaviconProps {
  /** The site's host, or the name the producer gave for it ("Facebook"). */
  host?: string;
  /** The result's URL; its hostname is used when `host` is a name rather than a host. */
  url?: string;
  alt: string;
  className?: string;
}

const TILE_CLASS = "flex size-6 shrink-0 items-center justify-center rounded-[5px]";

/**
 * A 24px site icon for a search result. When the site has no icon (the service answers with a small
 * placeholder, or the image fails), it falls back to a tile with the site's initial on a colour picked from
 * its host, and to a globe tile when there is no site to name.
 */
function Favicon({ host, url, alt, className }: FaviconProps) {
  const iconHost = getIconHost(host, url);
  const [failed, setFailed] = useState(false);

  if (!iconHost || failed) {
    const initial = getSiteInitial(host, iconHost);
    if (initial) {
      return (
        <div
          aria-hidden={alt === "" || undefined}
          aria-label={alt || undefined}
          className={cn(TILE_CLASS, "text-[11px] font-medium leading-none text-white", className)}
          data-testid="favicon-initial"
          role={alt ? "img" : undefined}
          style={{ backgroundColor: getSiteTileColor(iconHost ?? host ?? initial) }}
        >
          {initial}
        </div>
      );
    }
    return (
      <div className={cn(TILE_CLASS, "bg-white/10", className)} data-testid="favicon-fallback">
        <Icon className="size-4 text-foreground-primary" name="globe" />
      </div>
    );
  }

  return (
    // Third-party icon service; next/image would need every host allow-listed.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt={alt}
      className={cn("size-6 shrink-0 rounded-[5px] object-cover", className)}
      data-testid="favicon"
      loading="lazy"
      referrerPolicy="no-referrer"
      src={getFaviconUrl(iconHost)}
      onError={() => setFailed(true)}
      // The service's "no icon" placeholder loads fine but small; treat it as missing.
      onLoad={(event) => {
        if (event.currentTarget.naturalWidth < FAVICON_MIN_SIZE) setFailed(true);
      }}
    />
  );
}

export default Favicon;
